import { PageContentFactory } from 'src/factories/page-content';
import Store from '@store';
import { saveMetadata } from 'src/store/actions/metadata.action';

/* eslint-disable promise/catch-or-return */
export default class PagesGenerator {
  #tool;
  #editor;
  #canvas;
  #service;
  #newPages;
  #existedPages;
  #pageThumbnailFactory;
  #tableOfContents;
  #pageContentFactory = new PageContentFactory();

  constructor (pages) {
    this.#tool = INKIVE.Tool.getInstance();
    this.#editor = this.#tool.editor;
    this.#canvas = this.#editor.canvas;
    this.#service = INKIVE.PageFactory;
    this.#newPages = pages;
    this.#tableOfContents = Store.getState().metadata?.table_of_contents;

    this.#existedPages = this.#tableOfContents.pages?.map(page => page.id) || [];
    this.#pageThumbnailFactory = require('../../../../factory/page-thumbnail').default;
  }

  initialize () {
    const designs = INKIVE.ProjectFacade.get('designs').map(design => parseInt(design.design_id));
    this.#existedPages = this.#existedPages.filter(page => designs.includes(parseInt(page)));

    return this;
  }

  async generatePages () {
    if (this.#newPages.length > this.#existedPages.length) {
      await this.replacePages();
      await this.appendPages();
      await this.moveToCurrentPage();
    } else if (this.#newPages.length < this.#existedPages.length) {
      await this.replacePages();
      await this.deletePages();
      await this.moveToCurrentPage();
    } else {
      await this.replacePages();
      await this.moveToCurrentPage();
      this.updateMetadata(this.#existedPages);
    }

    await Store.dispatch(saveMetadata({ table_of_contents: this.#tableOfContents }));
    await this.#service.saveProjectData({ table_of_contents: this.#tableOfContents });

    INKIVE.ProjectFacade.refreshPageInfo();
  }

  async appendPages () {
    const pagesToAdd = this.#newPages.slice(this.#existedPages.length);
    const addedPages = await this.addBlankPages(pagesToAdd);

    this.updateMetadata(this.#existedPages.concat(addedPages.map(page => page.design_id)));
    await Promise.all(addedPages.map(page => this.#service.savePage(page.design_id, this.#pageContentFactory.sanitizeSvgString(this.#canvas.svgToString(page.pageSvg)))));
    await this.updateSidePanel(addedPages);
  }

  async deletePages () {
    const pagesToDelete = this.#existedPages.slice(this.#newPages.length);
    this.updateMetadata(this.#existedPages.slice(0, this.#newPages.length));
    for (let index = 0; index < pagesToDelete.length; index++) {
      const response = await this.#service.deletePage(pagesToDelete[index]);

      if (!response) return;

      const result = Array.isArray(response) ? response.find(res => res !== undefined) : response;

      if (!result || !(result.status === 'success' || Object.keys(result).length === 0)) return;

      if (INKIVE.Tool._current_page_number === INKIVE.C.BOOK.num_pages) {
        INKIVE.Tool._current_page_number--;
      }
      INKIVE.C.BOOK.num_pages--;
      if (parseInt(INKIVE.Tool._current_design_id) === parseInt(pagesToDelete[index])) {
        INKIVE.EditorFacade.handle('page', 'unload', { remove_from_cache: true });
      }
      await INKIVE.ProjectFacade.refreshPageInfo();
      await INKIVE.ProjectFacade.trigger('design.removed', { page_number: this.#existedPages.length + index + 1, quite: false, design_id: pagesToDelete[index] + '' });
    }
  }

  replacePages () {
    const newPagesToReplace = this.#newPages.length >= this.#existedPages.length
      ? this.#newPages.slice(0, this.#existedPages.length)
      : this.#newPages;
    const existedPagesToReplace = this.#newPages.length < this.#existedPages.length
      ? this.#existedPages.slice(0, this.#newPages.length)
      : this.#existedPages;
    return Promise.all(existedPagesToReplace.map(async (pageId, index) => {
      const pageContent = this.#pageContentFactory.sanitizeSvgString(this.#canvas.svgToString(newPagesToReplace[index]));
      INKIVE.EditorFacade.trigger('page.saved.content', {
        id: Number(pageId),
        content: pageContent
      });
      return this.#service.savePage(pageId, pageContent);
    }));
  }

  async addBlankPages (pagesToAdd) {
    const addedPages = [];
    const indexToAdd = INKIVE.ProjectFacade.get('designs')
      .filter(page => this.#existedPages.map(Number).includes(parseInt(page.id)))
      .map(page => parseInt(page.page_number))
      .reduce((a, b) => Math.max(a, b), 0);

    const pageNumberToAdd = indexToAdd ? indexToAdd + 1 : this.#existedPages.length + 1;

    for (let index = 0; index < pagesToAdd.length; index++) {
      const pageSvg = pagesToAdd[index];
      const { width, height, unit } = INKIVE.Helpers.getInlineSize(pageSvg);
      const params = { size: { width, height, unit }, quite: true, pageSvg };
      const response = await this.#service.createNewPage({
        width,
        height,
        unit,
        page_number: pageNumberToAdd + index
      });

      if (response && response.status === 'success') {
        INKIVE.C.BOOK.num_pages++;
        addedPages.push(Object.assign({}, response, params));
      }
    }

    return addedPages;
  }

  updateMetadata (pages) {
    this.#existedPages = pages;
    const temp = [];
    for (const page of pages) {
      temp.push({
        id: Number(page),
        position: this.#tableOfContents.pages.find(p => p.id === page)?.position || temp[temp.length - 1]?.position
      });
    }
    this.#tableOfContents = {
      ...this.#tableOfContents,
      pages: temp.slice()
    };
  }

  async updateSidePanel (pages) {
    for (const page of pages) {
      const pageContent = this.#pageContentFactory.sanitizeSvgString(this.#canvas.svgToString(page.pageSvg));
      const addedPage = Object.assign({}, page, { pageSvg: pageContent });
      await INKIVE.ProjectFacade.trigger('design.added', addedPage);
      await INKIVE.EditorFacade.handle('header_and_footer', 'create_page', {
        page_id: Number(page.design_id),
        page_number: page.page_number,
        skip_range: true
      });
      INKIVE.EditorFacade.trigger('page.saved.content', {
        id: Number(page.design_id),
        content: pageContent
      });
    }
  }

  async moveToCurrentPage () {
    const param = { save: false, design_id: INKIVE.Tool._current_design_id.toString(), quite: true };
    await INKIVE.EditorFacade.handle('page', 'move_to', param);
    INKIVE.EditorFacade.handle('page', 'save', { quiet: true });
  }
}
